/**
 * The Executive Dashboard's management in the admin (CR-008 §4.3, §3.1 item 10): who may open
 * dashboard.viganium.net, and its settings. An Administrator's — `dashboard.access.manage` and the
 * Administrator role — and no dashboard session is needed, which is how the first grant is made.
 */

export type AccessState = 'GRANTED' | 'NOT_GRANTED' | 'ENDED';
export type AccessEventKind = 'GRANTED' | 'REVOKED' | 'ROLE_REMOVED' | 'DEACTIVATED' | 'INELIGIBLE';

export interface PersonBrief {
  id: string;
  fullName: string;
  fullNameAr: string | null;
}

export interface AccessRow {
  user: { id: string; email: string; fullName: string; fullNameAr: string | null; employeeCode: string | null; jobTitle: string | null; isActive: boolean };
  roles: Array<{ code: string; name: string; nameAr: string | null }>;
  administrator: boolean;
  canGrant: boolean;
  ineligibleBecause: string[];
  access: {
    state: AccessState;
    grantedAt: string | null;
    grantedBy: PersonBrief | null;
    grantReason: string | null;
    endedAt: string | null;
    endedBy: PersonBrief | null;
    endReason: string | null;
    endedBecause: AccessEventKind | null;
  };
  stale: boolean;
  lastDashboardSignInAt: string | null;
  activeDashboardSessions: number;
}

export interface AccessPerson extends AccessRow {
  history: Array<{ id: string; kind: AccessEventKind; reason: string | null; createdAt: string; actor: PersonBrief | null }>;
}

export const DIMENSIONS = ['completion', 'deadline', 'quality', 'tickets', 'collaboration'] as const;
export type Dimension = (typeof DIMENSIONS)[number];
export const PILLARS = ['financial', 'work', 'delivery', 'service'] as const;
export type Pillar = (typeof PILLARS)[number];

export interface DimensionWeights {
  weights: Record<Dimension, number>;
  enabled: Record<Dimension, boolean>;
}

export interface ExecutiveConfig {
  scoring: DimensionWeights & {
    minEvidenceEvents: number;
    minMeasurableWeightPercent: number;
    reviewOverdueHours: number;
    labels: { excellent: number; veryGood: number; good: number };
    departmentOverrides: Array<DimensionWeights & { departmentId: string }>;
  };
  health: { weights: Record<Pillar, number>; minMeasurableWeightPercent: number; minScoredEmployees: number; minTickets: number; minDatedTasks: number };
  risks: {
    blockedQTaskDays: number;
    stalledWorkflowDays: number;
    contractExpiryDays: number;
    overdueReceivableSharePercent: number;
    expenseSpikePercent: number;
    receivableConcentrationPercent: number;
  };
  ai: {
    enabled: boolean;
    model: string | null;
    monthlyTokenBudget: number | null;
    reports: { daily: boolean; weekly: boolean; monthly: boolean; employees: boolean };
    consent: { confirmedAt: string | null; confirmedById: string | null };
    priceEstimate: { inputPerMillion: number | null; outputPerMillion: number | null; currency: string; asOf: string | null };
  };
  schedule: { dailyAt: string; monthlyAt: string; weekly: boolean; settleDays: number };
  retention: { snapshotMonths: number; exportLogMonths: number };
}

export interface ConfigVersion {
  version: number;
  config: ExecutiveConfig;
  effectiveFrom: string | null;
  createdAt: string | null;
  createdBy: PersonBrief | null;
}

export interface SettingsRead {
  current: ConfigVersion;
  latest: ConfigVersion;
  history: Array<{ version: number; effectiveFrom: string | null; createdAt: string | null; createdBy: PersonBrief | null }>;
  server: { ai: { enabled: boolean; configured: boolean; models: string[]; defaultModel: string | null; monthlyTokenBudget: number; maxConcurrentJobs: number } };
}

export interface ConfigProblem {
  field: string;
  reason: string;
}
